/**
 * The single pass/fail authority.
 *
 * Both the capture app and the report generator import this module. That is the
 * point: a verdict shown to an engineer on site and a verdict printed on the
 * certificate must be produced by the same code, or a report can contradict the
 * screen the reading was typed into.
 *
 * Verdicts are computed from the template item's own data (nominal, tolerance,
 * toleranceMode) and never read from a stored verdict field.
 */

import type { ResultLine, TemplateItem, Verdict } from './types.js';

export interface Band {
  min?: number;
  max?: number;
}

export interface VerdictResult {
  verdict: Verdict;
  /** Human-readable justification, shown in the UI and printable in the report. */
  reason: string;
  band?: Band;
}

export interface ResultInput {
  /** For measured checks. null/undefined means "not yet taken". */
  measuredValue?: number | null;
  /** For non-measured checks, the engineer's explicit selection. */
  selected?: Verdict | null;
}

/**
 * Floating point makes exact boundaries unreliable: 240 * 1.1 is not exactly 264
 * in IEEE 754. A reading sitting precisely on the tolerance limit must pass, so
 * comparisons are made with a relative epsilon rather than bare < and >.
 */
const EPSILON = 1e-9;

function lte(a: number, b: number): boolean {
  return a <= b + Math.abs(b) * EPSILON + EPSILON;
}

function gte(a: number, b: number): boolean {
  return a >= b - Math.abs(b) * EPSILON - EPSILON;
}

function round(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * The acceptable range for a measured check, or null when the item carries no
 * usable acceptance criteria.
 */
export function bandFor(item: TemplateItem): Band | null {
  const { nominal, tolerance, toleranceMode } = item;

  switch (toleranceMode) {
    case 'percent': {
      if (nominal === undefined || tolerance === undefined) return null;
      const delta = (nominal * tolerance) / 100;
      return { min: round(nominal - delta), max: round(nominal + delta) };
    }
    case 'absolute': {
      if (nominal === undefined || tolerance === undefined) return null;
      return { min: round(nominal - tolerance), max: round(nominal + tolerance) };
    }
    case 'max': {
      if (tolerance === undefined) return null;
      return { max: round(tolerance) };
    }
    case 'min': {
      if (tolerance === undefined) return null;
      return { min: round(tolerance) };
    }
    case 'none':
      return null;
  }
}

/** Renders the band the way it should read on screen and in the report. */
export function describeBand(item: TemplateItem): string {
  const band = bandFor(item);
  const unit = item.unit ? ` ${item.unit}` : '';
  if (!band) return item.acceptanceCriteria ?? 'no numeric criteria';
  if (band.min !== undefined && band.max !== undefined) {
    return `${band.min}${unit} to ${band.max}${unit}`;
  }
  if (band.max !== undefined) return `≤ ${band.max}${unit}`;
  if (band.min !== undefined) return `≥ ${band.min}${unit}`;
  return 'no numeric criteria';
}

function evaluateMeasured(item: TemplateItem, value: number): VerdictResult {
  if (!Number.isFinite(value)) {
    return { verdict: 'Pending', reason: 'measured value is not a number' };
  }

  const band = bandFor(item);
  if (!band) {
    return {
      verdict: 'Pending',
      reason: `no acceptance criteria on "${item.label}" — cannot compute a verdict`,
    };
  }

  const unit = item.unit ? ` ${item.unit}` : '';
  const withinMin = band.min === undefined || gte(value, band.min);
  const withinMax = band.max === undefined || lte(value, band.max);

  if (withinMin && withinMax) {
    return { verdict: 'Pass', reason: `${value}${unit} within ${describeBand(item)}`, band };
  }
  return { verdict: 'Fail', reason: `${value}${unit} outside ${describeBand(item)}`, band };
}

/**
 * The verdict for one check.
 *
 * A missing reading is Pending, never Fail — an untested check and a failed
 * check are different facts, and conflating them puts false deficiencies in the
 * report.
 */
export function evaluate(item: TemplateItem, input: ResultInput): VerdictResult {
  if (input.selected === 'N/A') {
    return { verdict: 'N/A', reason: 'marked not applicable' };
  }

  if (item.responseType === 'measured_pass_fail') {
    const value = input.measuredValue;
    if (value === undefined || value === null) {
      const band = bandFor(item);
      return { verdict: 'Pending', reason: 'not yet measured', ...(band ? { band } : {}) };
    }
    return evaluateMeasured(item, value);
  }

  // Every other response type is judged by the engineer, not computed.
  if (input.selected === undefined || input.selected === null) {
    return { verdict: 'Pending', reason: 'not yet answered' };
  }
  return { verdict: input.selected, reason: 'recorded by engineer' };
}

export function verdictOf(item: TemplateItem, input: ResultInput): Verdict {
  return evaluate(item, input).verdict;
}

/** Recomputes a stored result line's verdict from its template item. */
export function verdictForLine(item: TemplateItem, line: ResultLine): VerdictResult {
  return evaluate(item, {
    measuredValue: line.measuredValue ?? null,
    selected: line.result,
  });
}

export interface SectionRollup {
  total: number;
  pass: number;
  fail: number;
  na: number;
  pending: number;
  /** Pending checks are excluded — a partly tested section has no overall result yet. */
  overall: Verdict;
}

/** Rolls individual verdicts up to a section or instance result. */
export function rollup(verdicts: readonly Verdict[]): SectionRollup {
  const count = (v: Verdict) => verdicts.filter((x) => x === v).length;
  const pass = count('Pass');
  const fail = count('Fail');
  const na = count('N/A');
  const pending = count('Pending');

  let overall: Verdict;
  if (fail > 0) overall = 'Fail';
  else if (pending > 0) overall = 'Pending';
  else if (pass > 0) overall = 'Pass';
  else overall = 'N/A';

  return { total: verdicts.length, pass, fail, na, pending, overall };
}
